import { IAdminClassroomLink, IAdminKioskLink } from 'components/admin/unwired';
import { AssignmentStatus } from 'lib/types/assignment';
import { IModule, ModuleType } from 'lib/types/course';
import { SlideContentFormat } from 'lib/types/nudge/slideTypes';
import {
  PhishingCampaignStatus,
  PhishingTrainingCampaignStatus,
} from 'lib/types/phishing';
import { AutomatedSchedulerState } from 'lib/types/scheduling';
import {
  ILocation,
  IUserPopulationAttributes,
  UserProfileRole,
} from 'lib/types/user';
import { AdminAssignmentEntity, IAdminAssignmentReason } from '../types';

export enum AssignmentState {
  ARCHIVED = 'ARCHIVED',
  IN_PROGRESS = 'IN_PROGRESS',
  UPCOMING = 'UPCOMING',
}

export enum TrainingGoalContentType {
  ETHENA_CAMPAIGN = 'ETHENA_CAMPAIGN',
  CONTINUOUS_LEARNING = 'CONTINUOUS_LEARNING',
  USER_MANAGED = 'USER_MANAGED',
}

interface IAutomatedSchedulerPopulation {
  companyId: number;
  combinationOperator?: 'AND' | 'OR';
  // filters:
  departmentFilter?: { departments: string[]; exclude?: boolean };
  customCategoryFilter?: { customCategoryValues: string[]; exclude?: boolean };
  employeeTypeFilter?: { employeeTypes: string[]; exclude?: boolean };
  locationFilter?: { locations: ILocation[]; exclude?: boolean };
  roleFilter?: UserProfileRole;
  // overrides:
  excludeUserIds?: number[];
  alwaysIncludeUserIds?: number[];
}

export interface IAdminDashboardModule {
  id: number;
  courseId: number;
  companyId?: number;
  name: string;
  description: string;
  lengthInMinutes: number;
  previewUrl: string;
  type: ModuleType;
  belongsToModuleNames: string[];
  includedNudgeGroupNames: string[];
  isDeleted: boolean;
  publishDate: string;
}

export interface IAdminDashboardTrainingTrack {
  id: number;
  orderedModuleIds: number[];
  timerPercentage: number;
}

export interface IAdminDashboardTrainingGoal {
  contentType: TrainingGoalContentType;
  tracks: IAdminDashboardTrainingTrack[];
  courseIds: number[];
}

export interface IAdminAutomatedSchedulerConfiguration {
  assignmentKey: string;
  autoEnroll?: boolean;
  autoEnrollDeadlineInDays: number;
  classroomLinks?: IAdminClassroomLink[];
  companyId: number;
  courseId: number;
  customTrackOrderedModuleIds?: number[];
  deadlineInDays: number;
  deferralPeriodInMonths?: number;
  enrolledUserIds: number[];
  hasAssessmentCustomization: boolean;
  id: number;
  kioskLink?: IAdminKioskLink;
  name: string;
  population: IAutomatedSchedulerPopulation;
  recurringRemindersEnabled: boolean;
  repeatTraining: boolean;
  showTeamInsights: boolean;
  state: AutomatedSchedulerState;
  trainingDate: string;
  trainEveryonePeriodInMonths?: number;
  trainingGoal: IAdminDashboardTrainingGoal;
  wasScheduledAssignment: boolean;
}

export interface IAdminCompanyTrainingDate {
  id: number;
  companyId: number;
  courseId: number;
  trainingDate: string;
  archivedDate?: string;
  hasRemovableArchivedTraining: boolean;
  assignmentKey: string;
  trainingGoal: IAdminDashboardTrainingGoal;
}

export interface IAssignmentCompletionSummary extends ICompletionSummary {
  entity: AdminAssignmentEntity;
  id: number;
}

export interface ICompletionSummary {
  key: string;
  completed: number;
  withinDeadline: number;
  incomplete: number;
  neverCompleted: number;
}

export interface IDashboardAssignment {
  key: string;
  entity: AdminAssignmentEntity;
  entityId: number;
  companyId: number;
  courseId: number;
  deadlineInDays?: number;
  name: string;
  sentDate?: string;
  scheduledDate?: string;
  archivedDate?: string;
  learnerCount: number;
  upcomingLearnerCount?: number;
  automatedSchedulerConfig?: {
    autoEnroll: boolean;
    repeatTraining: boolean;
    deferralPeriodInMonths?: number;
  };
  phishingCampaign?: {
    phishingCampaignId: number;
  };
  isContinuousLearning: boolean;
  recurringRemindersEnabled: boolean;
  showTeamInsights: boolean;
}

export interface IDashboardLearnerInfo {
  userId: number;
  name: string;
  primaryEmail: string;
  isManager: boolean;
  department?: string;
  customCategory?: string;
  employeeType?: string;
  location: ILocation;
}

export interface IDashboardLearnerStatus {
  userId: number;
  name: string;
  primaryEmail: string;
  isManager: boolean;
  department?: string;
  customCategory?: string;
  employeeType?: string;
  location: ILocation;
  status: AssignmentStatus;
  latestStatusDate: string;
  latestAssignmentDate: string;
  latestAssignmentReason: IAdminAssignmentReason;
}

export interface IDashboardNudgeRatingsSummary {
  positiveRatingCount: number;
  negativeRatingCount: number;
}

export interface IDashboardPhishingTrainingCampaign {
  trainingCampaignId: number;
  assignmentKey: string;
  trainingStatus: PhishingTrainingCampaignStatus;
  trainingCampaignName: string;
  deadlineInDays: number;
  outstandingTrainingRemindersEnabled: boolean;
  teamInsightsEnabled: boolean;
  modules: IModule[];
  archivedAt?: string;
  phishingCampaign: {
    phishingCampaignId: number;
    status: PhishingCampaignStatus;
    sendingPeriodStartAt: string;
  };
}

export interface IEnrollmentCount {
  automatedSchedulerConfigId: number;
  assignedCount: number;
  upcomingCount: number;
}

export interface IModuleCorrectnessSummaryBlock {
  moduleName: string;
  rows: IModuleCorrectnessSummaryBlockRow[];
}

export interface IModuleCorrectnessSummaryBlockRow {
  text: string;
  percentCorrectFirstTry: number;
}

export interface INotificationSummary {
  totals: ICompletionSummary;
  assignments: IAssignmentCompletionSummary[];
}

export interface IAssignmentLearnerPopulationPayload {
  assignmentKey: string;
  populationFilter?: IUserPopulationAttributes;
  limit: number;
  offset: number;
  userSearchTerm?: string;
}

export interface IAdminDashboardCourseTrack {
  id: number;
  name: string;
  trainingDateFrequency: number;
  minLengthInMinutes: number;
  maxLengthInMinutes: number;
  orderedModules: IAdminTrainingTrackModule[];
}

export interface IAdminTrainingTrackModule {
  id: number;
  name: string;
}

export interface IAdminLearningLibraryModule {
  id: number;
  moduleId: number;
  moduleType: ModuleType;
  moduleName: string;
  courseId: number;
  courseName: string;
  publishedAt: string;
  formats: SlideContentFormat[];
  viewedCount: number;
  completionCount: number;
  last90DaysCompletionCount: number;
}

export interface IAdminLearningLibraryCourse {
  id: number;
  name: string;
}
